import { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card, Spinner, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { useTitle } from "./context";
import { getSpellHeaderEntries, loadState, storeState, useScrollRestore } from "./utilities";
import { SpellBookBackend } from "./backend";
import SpellHtmlContent from "./spellHtmlContent";

import "./index.css"


const SPELL_PAGE_KEY = "spell-page";


export function spellScrollStateKey(spellId) {
    return `${SPELL_PAGE_KEY}.${spellId}`
}


export function SpellPage({navbarHeight}) {
    const { spellId } = useParams();

    const { setTitle } = useTitle();
    const [spell, setSpell] = useState(null);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");
    
    const pageBodyRef = useRef(null);
    
    useScrollRestore(pageBodyRef, spellScrollStateKey(spellId));
    
    const setStateJson = (s) => {
        if (s.spellId !== spellId) throw new Error("Attempting to set a state with mismatching spell ID");
        setSpell(s.spell);
    };
    
    useEffect(() => {
        setTitle(spell?.name);
    }, [setTitle, spell]);

    useEffect(() => {
        if (spell) {
            const stateJson = {
                spellId: spell?.id,
                spell: spell
            };
            storeState(SPELL_PAGE_KEY, stateJson);
        }
    }, [spell, spellId]);

    const fetchData = async () => {
        setSpell(await SpellBookBackend.getSpell(spellId));
    }
    
    useEffect(() => {
        let mounted = true;
        (
            async () => {
                try {
                    setStatus("loading");
                    const s = loadState(SPELL_PAGE_KEY);
                    if (s?.spellId === spellId && s?.spell) {
                        setStateJson(s);
                    }
                    else {
                        await fetchData();
                    }
                    if (mounted) {
                        setStatus("success");
                    }
                }
                catch {
                    if (mounted) {
                        setError("Failed to load items.");
                        setStatus("error");
                    }
                }
            }
        )();
        return () => {
            mounted = false;
        };
    }, [spellId]);

    const content = useMemo(() => {
        if (status === "loading") {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status" aria-label="Loading items" />
                </div>
            );
        }
        if (status === "error") {
            return <Alert variant="danger" className="my-3">{error}</Alert>;
        }

        //else

        if (!spell) return;

        const entries = getSpellHeaderEntries(spell);

        return (
            <Container
            fluid
            className="pt-3 pb-3 bg-light">
                <Card className="mb-2 shadow-sm">
                    <Card.Body>
                        {entries.map((entry, idx) => (
                            <Row key={idx} className="mb-3">
                            <Col xs={12}>
                                <div className="fw-bold small fs-7 text-muted">{entry.title}</div>
                                <div className="fs-6">{entry.value}</div>
                            </Col>
                            </Row>
                        ))}
                    </Card.Body>
                    </Card>
            
                <Card className="shadow-sm">
                    <Card.Body style={{ whiteSpace: "pre-line" }}>
                        <SpellHtmlContent html={spell.description }/>
                    </Card.Body>
                </Card>
            </Container>
        );
    }, [spell]);

    const containerMargin = `${navbarHeight}px`;
    const containerHeight = `calc(100vh - ${navbarHeight}px)`;
    
    return (
        <Container
        ref={pageBodyRef}
        fluid
        className="bg-light p-0"
        style={{
            marginTop: containerMargin,
            height: containerHeight,
            overflowY: "auto"
        }}
        >
            {content}
        </Container>);
}
